import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { interval, of as observableOf } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CAAction, Organisations } from '../actions';
import { auth } from '../auth/auth.selectors';
import { Notifications } from './notifications.actions';
import * as selectors from '../../store/reducers/index';
import { CountDto, NotificationDto, NotificationsService } from '@careapp-group/ng-sdk';
import { LoopBackFilter } from '../action-factory';

@Injectable()
export class NotificationsEffects {
  dismissNotificationById = createEffect(() =>
    this.actions$.pipe(
      ofType(Notifications.REQUEST_DISMISS_NOTIFICATION),
      switchMap((action) => {
        return this.api
          .updateNotification({
            id: action.payload.id,
            notificationDto: { dismissed: true } as NotificationDto,
          })
          .pipe(
            map((response) => Notifications.ResponseSuccessDismissNotification(response)),
            catchError((err) => {
              return observableOf(Notifications.ResponseFailDismissNotification(err));
            }),
          );
      }),
    ),
  );

  getNotifications = createEffect(() =>
    this.actions$.pipe(
      ofType(Notifications.REQUEST_ALL_PAGINATED),
      withLatestFrom(
        this.store.pipe(select(auth.getLoggedInUser)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      ),
      filter(([action, user, organisation]) => !!user),
      switchMap(([action, user, organisation]) => {
        const filter: LoopBackFilter = {
          limit: action.payload.limit,
          skip: action.payload.skip,
          where: { dismissed: false },
        };
        return this.api
          .listNotifications({
            skip: action.payload.skip,
            limit: action.payload.limit,
            organisationId: organisation,
            dismissed: false,
          })
          .pipe(
            map((response) => {
              return Notifications.ResponseSuccessAllPaginatedAction({
                filter,
                paginator: (action as any).paginator,
                items: response,
              });
            }),
            catchError((err) => {
              return observableOf(
                Notifications.ResponseFailAllPaginatedAction(err, (action as any).paginator),
              );
            }),
          );
      }),
    ),
  );

  getUnseenNotificationCount = createEffect(() =>
    this.actions$.pipe(
      ofType(Notifications.REQUEST_UNSEEN_NOTIFICATION_COUNT),
      withLatestFrom(
        this.store.pipe(select(auth.getLoggedInUser)),
        this.store.pipe(select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      ),
      filter(([action, user]) => !!user),
      switchMap(([action, user, organisationId]) => {
        return this.api
          .countNotifications({ seen: false, organisationId: organisationId, dismissed: false })
          .pipe(
            map((response: CountDto) =>
              Notifications.ResponseSuccessUnseenNotificationCount(response.count),
            ),
            catchError((err) => {
              return observableOf(Notifications.ResponseFailUnseenNotificationCount(err));
            }),
          );
      }),
    ),
  );

  setNotificationAsRead = createEffect((): any =>
    this.actions$.pipe(
      ofType(Notifications.REQUEST_SET_NOTIFICATION_READ),
      switchMap((action) => {
        return this.api
          .updateNotification({
            id: action.payload.id,
            notificationDto: { seen: true } as NotificationDto,
          })
          .pipe(
            map((response) => Notifications.ResponseSuccessSetAsRead(response)),
            catchError((err) => {
              return observableOf(Notifications.ResponseFailSetAsRead(err));
            }),
          );
      }),
    ),
  );

  dismissAllNotifications = createEffect((): any =>
    this.actions$.pipe(
      ofType(Notifications.REQUEST_DISMISS_ALL_NOTIFICATIONS),
      withLatestFrom(this.store.pipe(select(auth.getLoggedInUser))),
      filter(([action, user]) => !!user),
      switchMap(([action, user]) => {
        return this.api.dismissAllNotifications().pipe(
          map((response) => Notifications.ResponseSuccessDismissAllNotifications()),
          catchError((err) => {
            return observableOf(Notifications.ResponseFailDismissAllNotifications(err));
          }),
        );
      }),
    ),
  );

  refreshNotificationCountWhenOrgChanges = createEffect((): any =>
    this.actions$.pipe(
      ofType(Organisations.SELECT_BY_ID),
      map(() => Notifications.RequestUnseenNotificationCount()),
    ),
  );

  pollForUnseenNotifications$ = createEffect(() =>
    this.store.pipe(
      select(auth.getLoggedInUser),
      distinctUntilChanged(),
      switchMap((user) => interval(environment.notificationsPollInterval).pipe(mapTo(user))),
      filter((user) => !!user),
      mapTo(Notifications.RequestUnseenNotificationCount()),
    ),
  );

  constructor(
    private actions$: Actions<CAAction>,
    private api: NotificationsService,
    private store: Store<any>,
  ) {}
}
